// Filter state + derived filtered requests list.
// State is held in a single object so the memo only depends on [requests, scope, filters]
// (3 deps) — keeps cyclomatic complexity low and respects max-deps lint rules.
import { useCallback, useMemo, useState } from "react";

const DEFAULTS = {
  status: "submitted",
  restaurant: "all",
  vendor: "all",
  productId: "all",
  requester: "",
  from: "",
  to: "",
};

function matchesScope(r, scope) {
  if (scope === "purchase") return r.type === "purchase";
  if (scope === "transfer") return r.type === "internal_transfer";
  return true;
}

function matchesStatus(r, status) {
  if (status === "all") return true;
  if (status === "awaiting_delivery") return r.status === "approved" && !r.deliveryConfirmedAt;
  return r.status === status;
}

function matchesRestaurant(r, restaurant) {
  if (restaurant === "all") return true;
  return r.restaurant === restaurant || r.fromRestaurant === restaurant || r.toRestaurant === restaurant;
}

function matchesDateRange(r, fromMs, toMs) {
  const t = new Date(r.createdAt).getTime();
  if (fromMs !== null && t < fromMs) return false;
  if (toMs !== null && t > toMs) return false;
  return true;
}

function applyFilters(requests, scope, filters) {
  const { status, restaurant, vendor, productId, requester, from, to } = filters;
  const fromMs = from ? new Date(from).getTime() : null;
  const toMs = to ? new Date(to).getTime() + 24 * 60 * 60 * 1000 : null;
  const reqQ = requester.trim().toLowerCase();
  return requests.filter((r) => {
    if (!matchesScope(r, scope)) return false;
    if (!matchesStatus(r, status)) return false;
    if (!matchesRestaurant(r, restaurant)) return false;
    if (vendor !== "all" && r.vendor !== vendor) return false;
    if (productId !== "all" && !(r.items || []).some((it) => it.productId === productId)) return false;
    if (reqQ && !r.requestedByName?.toLowerCase().includes(reqQ)) return false;
    if (!matchesDateRange(r, fromMs, toMs)) return false;
    return true;
  });
}

export function useRequestFilters(requests, scope) {
  const [filters, setFilters] = useState(DEFAULTS);

  const update = useCallback((key) => (v) => setFilters((f) => ({ ...f, [key]: v })), []);
  const setters = useMemo(
    () => ({
      setStatus: update("status"),
      setRestaurant: update("restaurant"),
      setVendor: update("vendor"),
      setProductId: update("productId"),
      setRequester: update("requester"),
      setFrom: update("from"),
      setTo: update("to"),
    }),
    [update]
  );

  const filtered = useMemo(
    () => applyFilters(requests, scope, filters),
    [requests, scope, filters]
  );

  return { state: filters, setters, filtered };
}
